import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Loader2, Check } from "lucide-react";

const card = "bg-white rounded-[18px] border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]";
const CHIP = { green: "bg-emerald-50 text-emerald-700", amber: "bg-amber-50 text-amber-700", red: "bg-red-50 text-red-700", grey: "bg-slate-50 text-slate-400" };
const STATE = { green: "bg-emerald-50 text-emerald-700", amber: "bg-amber-50 text-amber-700", red: "bg-red-50 text-red-700" };
const FILTERS = [["todas", "Todas", "total"], ["risco", "Em risco", "risk"], ["falta", "Com falta", "falta"], ["prontas", "Prontas", "ready"]];
const keep = { todas: () => true, risco: (r) => r.tone === "red", falta: (r) => r.tone === "amber", prontas: (r) => r.tone === "green" };

function Row({ r }) {
  const hot = r.tone === "red" && r.days <= 7;
  return (
    <div className="grid grid-cols-[64px_minmax(0,1fr)] lg:grid-cols-[64px_minmax(0,1fr)_330px_200px_150px] items-center gap-x-5 gap-y-3 py-4 border-t border-slate-100 first:border-t-0" data-testid={`deed-row-${r.pid}`}>
      <div className={`w-16 h-16 rounded-xl border grid place-items-center text-center ${hot ? "border-red-200 bg-red-50" : "border-slate-200 bg-white"}`}>
        <div><div className="text-2xl font-bold navy-text leading-none">{r.day}</div><div className="text-xs font-semibold text-slate-600 mt-1">{r.wd}</div></div>
      </div>
      <div className="min-w-0">
        <Link to={`/processo/${r.pid}`} className="block text-[17px] font-semibold navy-text hover:underline truncate" data-testid={`deed-title-${r.pid}`}>
          {r.title || <>{r.typ ? `${r.typ} · ` : ""}<span className="text-red-700">morada por preencher</span></>}
        </Link>
        <div className="text-[15px] text-slate-500 truncate">{r.sub}</div>
      </div>
      <div className="col-span-2 lg:col-span-1 flex flex-wrap gap-1.5" data-testid={`deed-chips-${r.pid}`}>
        {r.chips.map((c) => <span key={c.label} className={`px-2 py-0.5 rounded-md text-sm font-semibold ${CHIP[c.tone]}`} data-testid={`deed-chip-${r.pid}-${c.label}`} data-tone={c.tone}>{c.label}</span>)}
      </div>
      <div className="col-span-2 lg:col-span-1">
        <span className={`inline-block px-3 py-1 rounded-full text-[15px] font-semibold ${STATE[r.tone]}`} data-testid={`deed-state-${r.pid}`}>{r.state}</span>
        {r.why && <div className="text-sm text-slate-500 mt-1">{r.why}</div>}
      </div>
      <Link to={`/processo/${r.pid}`} data-testid={`deed-open-${r.pid}`}
        className={`col-span-2 lg:col-span-1 h-12 grid place-items-center rounded-xl text-[15px] font-semibold transition-colors ${r.tone === "green" ? "border border-slate-300 text-slate-800 hover:bg-slate-50" : "bg-ec-green text-white hover:bg-ec-greenDark"}`}>
        {r.tone === "green" ? "Pasta" : "Abrir"}
      </Link>
    </div>
  );
}

export default function Escrituras() {
  const [d, setD] = useState(null);
  const [f, setF] = useState("todas");
  const [cons, setCons] = useState("");
  useEffect(() => { api.get("/gestao/escrituras").then(({ data }) => setD(data)); }, []);
  const consultants = useMemo(() => [...new Map((d?.groups || []).flatMap((g) => g.rows).map((r) => [r.consultant_id, r.consultant])).entries()], [d]);
  if (!d) return <div className="py-20 grid place-items-center text-slate-400"><Loader2 className="animate-spin" /></div>;
  const groups = d.groups.map((g) => ({ ...g, rows: g.rows.filter((r) => keep[f](r) && (!cons || r.consultant_id === cons)) })).filter((g) => g.rows.length);
  return (
    <div className="space-y-5 pt-4 sm:pt-6" data-testid="escrituras-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl navy-text">Escrituras</h1>
          <p className="text-[16px] text-slate-500 mt-1" data-testid="deeds-summary">{d.total} marcadas
            {d.risk > 0 && <> · <b className="text-red-700">{d.risk} em risco{d.risk_week ? ` (${d.risk_week} esta semana)` : ""}</b></>}
            {d.falta > 0 && <> · <b className="text-amber-700">{d.falta} com falta</b></>}
            {d.ready > 0 && <> · <b className="text-emerald-700">{d.ready} {d.ready === 1 ? "pronta" : "prontas"}</b></>}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {FILTERS.map(([k, l, n]) => (
            <button key={k} onClick={() => setF(k)} data-testid={`deeds-filter-${k}`}
              className={`h-11 px-4 rounded-xl text-[15px] font-semibold border transition-colors ${f === k ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-800 hover:bg-slate-50"}`}>{l} · {d[n]}</button>
          ))}
          <select value={cons} onChange={(e) => setCons(e.target.value)} className="h-11 px-3 rounded-xl border border-slate-300 bg-white text-[15px] font-semibold" data-testid="deeds-consultant">
            <option value="">Consultor</option>
            {consultants.map(([id, n]) => <option key={id} value={id}>{n}</option>)}
          </select>
        </div>
      </div>
      <div className={`rounded-xl px-4 py-3 text-[15px] ${d.unset ? "bg-amber-50 text-amber-800" : "bg-emerald-50 text-emerald-800"}`} data-testid="deeds-unset">
        {d.unset ? `${d.unset} ${d.unset === 1 ? "venda com CPCV ainda sem" : "vendas com CPCV ainda sem"} escritura marcada.` : <span className="inline-flex items-center gap-2"><Check size={16} /> Todas as vendas com CPCV assinado têm escritura marcada.</span>}
      </div>
      {!groups.length && <p className="text-slate-500" data-testid="deeds-empty">Nenhuma escritura com este filtro.</p>}
      {groups.map((g) => (
        <section key={g.key} className={`${card} px-5 md:px-7 py-4`} data-testid={`deeds-group-${g.key}`}>
          <div className="flex items-baseline justify-between pb-3 border-b border-slate-100">
            <h2 className="text-sm font-bold tracking-wider text-slate-600 uppercase">{g.label}</h2>
            <span className="text-sm text-slate-500">{g.key === "semana" ? d.week_range : `${g.rows.length} ${g.rows.length === 1 ? "escritura" : "escrituras"}`}</span>
          </div>
          {g.rows.map((r) => <Row key={r.pid} r={r} />)}
        </section>
      ))}
      <p className="text-sm text-slate-500">Verde: feito · âmbar: falta · vermelho: bloqueia · cinzento: não se aplica.</p>
    </div>
  );
}
